import { ReadonlyVec2 } from 'gl-matrix';

/**
 * Validation for the transient dose *display* settings — colorwash range,
 * display scale, and display normalization.
 *
 * These are UI/visualization parameters (see the per-dose display store),
 * not clinical domain state, so the rules live in the UI layer. The rules
 * themselves are unchanged from when they were enforced on the domain
 * entity, so SR-WOR-H-4-1 / SRV-WOR-H-4-1-1 continue to describe the
 * behaviour — only the enforcing layer moved. Predicates (reject-and-keep),
 * not throwers: the UI rejects an invalid edit and retains the previous
 * valid value, rather than raising.
 */

/** Dimensionless display multiplier: finite and strictly positive. */
export function isValidDoseScale(value: number): boolean {
    return Number.isFinite(value) && value > 0;
}

/** 100 %-normalization dose in Gy: finite and strictly positive. */
export function isValidNormalizationGy(value: number): boolean {
    return Number.isFinite(value) && value > 0;
}

/**
 * A dose as a percentage of the normalization dose, or undefined when the
 * normalization dose is not a finite dose above 0 Gy: no percentage of such
 * a dose exists.
 */
export function relativeDosePercent(
    doseGy: number,
    normalizationValueGy: number
): number | undefined {
    return isValidNormalizationGy(normalizationValueGy)
        ? (100 * doseGy) / normalizationValueGy
        : undefined;
}

/**
 * Colorwash window normalized to 0..1 of max dose: both bounds finite and
 * within [0, 1], with the minimum strictly below the maximum. An empty or
 * inverted window renders no colorwash with no indication why.
 */
export function isValidColorwashRange(range: ReadonlyVec2): boolean {
    const [min, max] = range;
    return (
        Number.isFinite(min) &&
        Number.isFinite(max) &&
        min >= 0 &&
        max <= 1 &&
        min < max
    );
}
